import { user } from 'ampersand-app'

/**
 * Backend-driven invoice review configuration, delivered via /user/self
 * (user.invoiceReviewConfig, derived from the API env vars
 * INVOICE_REVIEW_AUTO_APPROVE_VALID_XML / INVOICE_REVIEW_AUTO_CREATE_EXPENSE).
 */
export interface InvoiceReviewConfig {
  autoApproveValidXml: boolean
  autoCreateExpense: boolean
}

export const getInvoiceReviewConfig = (): InvoiceReviewConfig => ({
  autoApproveValidXml: !!user?.invoiceReviewConfig?.autoApproveValidXml,
  autoCreateExpense: !!user?.invoiceReviewConfig?.autoCreateExpense,
})

/**
 * FLAG:useDocuAi — OBSOLETE opt-in, always hidden. A completing / re-review save now ALWAYS creates
 * (or updates) the expense for a reviewed invoice — a reviewed invoice sitting in the expense list
 * without one is illogical (receiptReviewCaseService.resolveExpenseReviewPlan + fileService/update).
 * There is nothing left to opt into, so the "Ausgabe erstellen" checkbox is hidden in every review
 * surface (this helper's only callers — review page / drawer / embedded). A cancellation gets one
 * too: since PO 2026-09-15 the server turns a receipt whose STATED TOTAL is negative around as a
 * whole (relative signs inside it stay as printed) and books it on the ledger the booking target
 * names. Only a receipt CLASSIFIED as a credit note whose amounts are printed
 * POSITIVE is still refused server-side (`cancellationSignUnknown`), and that case needs no hidden
 * checkbox to say so either.
 * Params retained for the existing call sites.
 */
export const shouldShowCreateExpenseCheckbox = (
  _hasExpenseWriteRights: boolean,
  _isCancellation = false,
): boolean => false

interface BookingSectionsVisibility {
  useDocuAiEnabled: boolean
  isDocumentInvoiceReviewMode: boolean
  /** Has the TENANT subscribed to the accounting module? (useCheckModuleAccess) */
  hasExpenseModule: boolean
  /** May this USER create expenses? (useCheckAccessRights) */
  hasExpenseWriteRight: boolean
}

/**
 * FLAG:useDocuAi — may the review show the accounting-dependent sections?
 *
 * Product rule (QA TC-0301 / TC-0304): a tenant without accounting must see NO functionality in
 * the review that requires the accounting module. Two sections are affected:
 *
 *  - **Line items** — they exist only to book an expense from them. They are still saved; the server
 *    marks them unreviewed (`positionsReviewed:false`, `reviewScope:'document'`,
 *    `reviewStatus:'partial'`) so the customer can review them after purchasing the module later.
 *  - **Payment** — the card contains transfer / payment / assignment, i.e. booking interfaces
 *    (PaymentDrawerV2, AssignmentDrawer, BankTransferDrawer). Without the module it was visible and
 *    crashed when clicked. It is now omitted completely, including the receipt's payment details
 *    grouped into it, which are not a separate section.
 *
 * What remains: contact, receipt details, tags — the part of the review that makes sense without
 * accounting.
 *
 * Both questions deliberately live here: „has the tenant subscribed to the module" (subscription) and
 * „is this user allowed to do so" (role). Confusing them caused the defect fixed by this change —
 * server-side, `getReceiptReviewCapabilities` checked only the role and therefore considered a
 * tenant without accounting capable of creating expenses. This function mirrors the corrected
 * `receiptReviewCapabilities.canCreateExpenses` exactly; both sides must give the same answer or
 * the user sees sections the server writes back as unreviewed.
 *
 * ONE predicate for both sections — two copies of the same question would create exactly the
 * divergence warned about above.
 */
export const shouldHideBookingSectionsInReview = ({
  useDocuAiEnabled,
  isDocumentInvoiceReviewMode,
  hasExpenseModule,
  hasExpenseWriteRight,
}: BookingSectionsVisibility): boolean =>
  !!useDocuAiEnabled
  && !!isDocumentInvoiceReviewMode
  && !(hasExpenseModule && hasExpenseWriteRight)

interface BookingTargetChoiceVisibility {
  useDocuAiEnabled: boolean
  /** Does the upload carry an intent? (`metaData.receiptUploadAction`) */
  hasUploadIntent: boolean
  /** Has the TENANT subscribed to the revenue module? (useCheckModuleAccess) */
  hasRevenueModule: boolean
  /** May this USER create revenues? (useCheckAccessRights) */
  hasRevenueWriteRight: boolean
}

/**
 * FLAG:useDocuAi — may the review ask „should this be revenue?" (plan `review-booking-target`)
 *
 * The callers are all three review hosts (page, drawer tab, embedded receipt review). Upload origin
 * supplies the initial target, but an explicit reviewer choice may correct it.
 *
 * Two reasons NOT to ask it:
 *
 *  - **No revenue MODULE** (tenant without accounting or with expenses only).
 *  - **No revenue PERMISSION** for this user.
 *
 * The last two exactly match the server-side `canCreateRevenues`
 * (`api/src/services/receiptReviewAuthorization.ts`: role AND module). Do NOT confuse them —
 * `useCheckModuleAccess` = „has the TENANT subscribed to the module", `useCheckAccessRights` = „is
 * this USER allowed to do so". This exact confusion caused the defect fixed by
 * `shouldHideBookingSectionsInReview` above, hence the same construction here.
 *
 * Visibility is convenience, not security: the server independently enforces the same rule.
 * Without the module/permission, `resolveRevenueReviewPlan` exits with `missingRevenueAccess`.
 */
export const shouldShowBookingTargetChoice = ({
  useDocuAiEnabled,
  hasRevenueModule,
  hasRevenueWriteRight,
}: BookingTargetChoiceVisibility): boolean =>
  !!useDocuAiEnabled
  && !!hasRevenueModule
  && !!hasRevenueWriteRight

/**
 * FLAG:useDocuAi — may the choice offer „Nur Dokument“ at all? Decision Ralf, 2026-09-16.
 *
 * The ledger stays correctable (expense ⇄ revenue — a credit note handed in through Ausgaben is the
 * case that actually happens), but „Nur Dokument“ is withdrawn wherever the receipt came in through
 * a finance entry point. Offering it there asks the user to undo, from inside the review, the very
 * statement their upload made.
 *
 * The rule is the UPLOAD INTENT, not the host — deliberately. „Über Ausgaben hochgeladen“ reaches
 * the review through two different surfaces: the upload inside the expense/revenue FORM opens the
 * embedded review, while the dropzone on the LIST creates a row whose click navigates to
 * `/documents/review/:id?returnTo=/expenses`, i.e. the Documents review page. Gating on the host
 * would have covered the first and missed the second — which is exactly what happened when this was
 * first built. `receiptUploadAction` travels with the file and answers the same in both.
 *
 * An upload through DOCUMENTS carries no intent and keeps all three options: there the ledger
 * genuinely is open, and „Nur Dokument“ is the preselected answer (resolveUndecidedBookingTarget).
 *
 * The detached/restoring receipt is excluded from „has intent“ by its callers for the same reason as
 * in `resolveUndecidedBookingTarget`: its intent describes where it is being restored TO, and a
 * removed booking must stay able to be document-only.
 */
export const shouldOfferDocumentBookingTarget = ({
  hasUploadIntent,
}: {
  hasUploadIntent: boolean
}): boolean => !hasUploadIntent

/**
 * FLAG:useDocuAi — the stored decision, minus the value the surface cannot show.
 *
 * Display and save read the SAME resolved target, so a stored `'document'` on a surface that no
 * longer offers it would leave the radio group with nothing checked while the save still wrote
 * 'document'. Such records exist: until this change the choice was rendered even for a receipt with
 * an upload intent, so somebody could pick „Nur Dokument“ on one.
 */
export const stripUnofferedDocumentTarget = (
  storedBookingTarget: string | null | undefined,
  offersDocumentTarget: boolean,
): string | null | undefined =>
  !offersDocumentTarget && storedBookingTarget === 'document' ? null : storedBookingTarget

interface UndecidedBookingTargetInput {
  useDocuAiEnabled: boolean
  /**
   * The reviewer's persisted decision (`documentInvoiceDetails.bookingTarget`). Absent/null while
   * nobody has decided — and ONLY then is there a default to apply.
   */
  storedBookingTarget?: string | null
  /**
   * The intent the upload carried (`metaData.receiptUploadAction`) — `createExpense` from the
   * expenses dropzone, `createRevenue` for a recognized credit note.
   */
  receiptUploadAction?: string | null
  /**
   * The ledger a detached receipt is being restored to
   * (`metaData.receiptAccountingRestoreTarget`) — an expense/revenue restore is just as explicit
   * as an upload intent.
   */
  receiptAccountingRestoreTarget?: string | null
  /** Is an expense/revenue already linked to this document? (`file.expense` / `file.revenue`) */
  hasLinkedReceipt?: boolean
  /** Has the TENANT subscribed to the documents module? (`checkModuleAccess`/`useCheckModuleAccess`) */
  hasDocumentsModule: boolean
}

/**
 * FLAG:useDocuAi — what is PRESELECTED in the booking-target choice while nobody has decided yet?
 * Product decision Ralf, 2026-09-16: „Nur Dokument". Nothing is booked until a reviewer actively
 * picks expense or revenue; the old default (expense, via the payee role in
 * `resolveReviewReceiptType`) booked a receipt for anyone who just saved the form.
 *
 * `'document'` is a PRESELECTION, not a decision: it enters the host ladder in the same slot the
 * persisted decision uses (`bookingTargetChoice ?? … ?? finalData.bookingTarget ?? evidence`), so a
 * reviewer click still wins. Returning `null` means „undecided as before" and leaves the legacy
 * fall-through (hard evidence → payee role → expense) completely untouched.
 *
 * Five situations keep the old default, each for its own reason:
 *
 *  - **FLAG OFF** — the whole booking-target choice is a `useDocuAi` feature; with the flag off
 *    there is no radio group and no third state, so the flag is part of the condition itself rather
 *    than an assumption about who calls this (same construction as `shouldShowBookingTargetChoice`).
 *  - **A stored decision exists** — then this is not a preselection question at all.
 *  - **A receipt is already linked** (`file.expense` / `file.revenue`). Old documents carry no
 *    `bookingTarget` at all — the column predates the choice — so without this the re-review of an
 *    already booked invoice would open on „Nur Dokument" and DETACH the existing receipt on save.
 *    The default exists to stop an unintended booking, never to undo a deliberate one.
 *  - **The receipt carries an explicit intent** (`receiptUploadAction`, or an accounting
 *    restore target). Whoever already stated their intent gets it proposed back — dropping someone
 *    who uploaded through „Ausgabe erfassen" onto „Nur Dokument" would throw that statement away.
 *    Note WHY this must be decided here and not left to `resolveReviewBookingTarget`: that function
 *    checks the three-state target FIRST, so a defaulted `'document'` would silently outrank the
 *    upload intent below it.
 *  - **No documents module** — `BookingTargetRadio` then offers expense/revenue only, so
 *    preselecting `'document'` would leave the radio group with nothing checked AND send an
 *    unreachable target to the save. An unselectable preselection is worse than the old default.
 */
export const resolveUndecidedBookingTarget = ({
  useDocuAiEnabled,
  storedBookingTarget,
  receiptUploadAction,
  receiptAccountingRestoreTarget,
  hasLinkedReceipt,
  hasDocumentsModule,
}: UndecidedBookingTargetInput): 'document' | null => {
  if (!useDocuAiEnabled) { return null }
  if (storedBookingTarget) { return null }
  if (hasLinkedReceipt) { return null }
  if (receiptUploadAction) { return null }
  if (receiptAccountingRestoreTarget === 'expense' || receiptAccountingRestoreTarget === 'revenue') { return null }
  if (!hasDocumentsModule) { return null }

  return 'document'
}
